import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, CheckCircle, Lock, Eye, Key } from 'lucide-react';

interface SecuriteAccesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SecuriteAccesModal({ isOpen, onClose }: SecuriteAccesModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-red-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-red-500/30 shadow-2xl shadow-red-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-red-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl">
                    <Shield className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">
                      Sécurité & Accès
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Accès sécurisés physiques et virtuels - Protection complète
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-red-600/20 via-orange-600/20 to-red-600/20 rounded-xl p-8 border-2 border-red-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  Sécurité Complète : Physique et Virtuelle
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  Les accès sont <strong className="text-white">sécurisés</strong> tant au niveau 
                  <strong className="text-red-400"> physique</strong> que <strong className="text-orange-400">virtuel</strong>. 
                  Contrôles d'accès stricts, monitoring continu, protection complète de votre infrastructure.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-xl p-6 border border-red-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Lock className="w-7 h-7 text-red-400" />
                    Sécurité Physique
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Contrôles d'accès stricts aux centres de données :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Contrôles d'accès</strong> - Badges, codes, biométrie</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Surveillance vidéo</strong> - Monitoring 24/7</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Zones sécurisées</strong> - Accès restreint aux salles serveurs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Registre d'accès</strong> - Traçabilité complète</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Key className="w-7 h-7 text-orange-400" />
                    Sécurité Virtuelle
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Protection complète de l'accès virtuel :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Authentification forte</strong> - MFA, tokens</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Chiffrement</strong> - Données en transit et au repos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">VPN sécurisé</strong> - Accès à distance protégé</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Contrôle d'accès réseau</strong> - Firewalls, segmentation</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Eye className="w-7 h-7 text-indigo-400" />
                  Monitoring Continu
                </h4>
                <p className="text-gray-300 mb-4">
                  Surveillance et monitoring en temps réel :
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">Surveillance 24/7</strong> - Monitoring continu</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">Alertes en temps réel</strong> - Notification immédiate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">Logs d'activité</strong> - Traçabilité complète</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">Analyse de sécurité</strong> - Détection d'anomalies</span>
                  </li>
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-red-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl hover:from-red-600 hover:to-orange-600 transition-all duration-200 font-semibold shadow-lg shadow-red-500/20 hover:shadow-red-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












